import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Brand, CreateBrandPayload } from '@/api/brandApi';
import type { Category, CreateCategoryPayload } from '@/api/categoryApi';

type DeleteTarget =
  | { kind: 'brand'; entity: Brand }
  | { kind: 'category'; entity: Category };

interface CatalogManagementSectionProps {
  brands: Brand[];
  categories: Category[];
  isBrandLoading: boolean;
  isCategoryLoading: boolean;
  brandError: string;
  categoryError: string;
  pendingAction: string | null;
  onRefresh: () => void | Promise<void>;
  onSaveBrand: (input: CreateBrandPayload, editingBrand: Brand | null) => Promise<boolean>;
  onDeleteBrand: (brand: Brand) => Promise<boolean>;
  onSaveCategory: (
    input: CreateCategoryPayload,
    editingCategory: Category | null,
  ) => Promise<boolean>;
  onDeleteCategory: (category: Category) => Promise<boolean>;
}

const CatalogManagementSection: React.FC<CatalogManagementSectionProps> = ({
  brands,
  categories,
  isBrandLoading,
  isCategoryLoading,
  brandError,
  categoryError,
  pendingAction,
  onRefresh,
  onSaveBrand,
  onDeleteBrand,
  onSaveCategory,
  onDeleteCategory,
}) => {
  const { t } = useTranslation();
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [brandName, setBrandName] = useState('');
  const [brandFormError, setBrandFormError] = useState('');
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [categoryFormError, setCategoryFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const isBusy = pendingAction !== null;

  useEffect(() => {
    if (!deleteTarget) return undefined;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isBusy) setDeleteTarget(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [deleteTarget, isBusy]);

  const resetBrandForm = () => {
    setEditingBrand(null);
    setBrandName('');
    setBrandFormError('');
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryName('');
    setCategoryDescription('');
    setCategoryFormError('');
  };

  const startEditingBrand = (brand: Brand) => {
    setEditingBrand(brand);
    setBrandName(brand.name);
    setBrandFormError('');
  };

  const startEditingCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setCategoryDescription(category.description ?? '');
    setCategoryFormError('');
  };

  const submitBrand = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = brandName.trim();
    if (!name) {
      setBrandFormError(t('admin.products.catalog.validation.brand_name'));
      return;
    }

    setBrandFormError('');
    const didSave = await onSaveBrand({ name }, editingBrand);
    if (didSave) resetBrandForm();
  };

  const submitCategory = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = categoryName.trim();
    const description = categoryDescription.trim();
    if (!name) {
      setCategoryFormError(t('admin.products.catalog.validation.category_name'));
      return;
    }
    if (!description) {
      setCategoryFormError(t('admin.products.catalog.validation.category_description'));
      return;
    }

    setCategoryFormError('');
    const didSave = await onSaveCategory({ name, description }, editingCategory);
    if (didSave) resetCategoryForm();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const didDelete = deleteTarget.kind === 'brand'
      ? await onDeleteBrand(deleteTarget.entity)
      : await onDeleteCategory(deleteTarget.entity);

    if (!didDelete) return;
    if (deleteTarget.kind === 'brand' && editingBrand?.id === deleteTarget.entity.id) {
      resetBrandForm();
    }
    if (deleteTarget.kind === 'category' && editingCategory?.id === deleteTarget.entity.id) {
      resetCategoryForm();
    }
    setDeleteTarget(null);
  };

  const renderListState = (
    isLoading: boolean,
    error: string,
    isEmpty: boolean,
    emptyKey: 'categories' | 'brands',
  ) => {
    if (isLoading) {
      return (
        <div className="flex min-h-40 items-center justify-center gap-2 text-secondary" role="status">
          <span className="material-symbols-outlined animate-spin">progress_activity</span>
          <span>{t('admin.products.catalog.loading')}</span>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex min-h-40 flex-col items-center justify-center gap-2 px-4 text-center" role="alert">
          <span className="material-symbols-outlined text-3xl text-error">cloud_off</span>
          <p className="text-body-sm text-error">{error}</p>
          <button
            type="button"
            className="font-label-sm text-primary hover:underline"
            onClick={() => void onRefresh()}
          >
            {t('admin.products.actions.retry')}
          </button>
        </div>
      );
    }
    if (isEmpty) {
      return (
        <div className="flex min-h-40 flex-col items-center justify-center gap-1 px-4 text-center text-secondary">
          <span className="material-symbols-outlined text-3xl text-outline">inventory_2</span>
          <p className="font-medium text-on-surface">{t(`admin.products.catalog.empty.${emptyKey}_title`)}</p>
          <p className="text-body-sm">{t('admin.products.catalog.empty.description')}</p>
        </div>
      );
    }
    return null;
  };

  const deleteTargetName = deleteTarget?.entity.name ?? '';
  const deletePendingKey = deleteTarget
    ? `${deleteTarget.kind}-delete-${deleteTarget.entity.id}`
    : '';

  return (
    <section className="min-w-0 overflow-hidden rounded-[20px] border border-outline-variant/70 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-outline-variant bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="min-w-0">
          <h2 className="font-h3 text-[20px] text-on-surface sm:text-h3">
            {t('admin.products.catalog.title')}
          </h2>
          <p className="mt-0.5 text-body-sm text-secondary">
            {t('admin.products.catalog.description')}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-body-sm text-secondary sm:flex-nowrap sm:gap-3">
          <span>{t('admin.products.catalog.summary', { categoryCount: categories.length, brandCount: brands.length })}</span>
          <button
            type="button"
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-outline-variant bg-white px-3 font-label-md text-secondary hover:bg-surface-container-low disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isBrandLoading || isCategoryLoading || isBusy}
            onClick={() => void onRefresh()}
          >
            <span className="material-symbols-outlined text-[17px]">refresh</span>
            {t('admin.products.catalog.refresh')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 divide-y divide-outline-variant xl:grid-cols-2 xl:divide-x xl:divide-y-0">
        <article className="min-w-0">
          <header className="border-b border-outline-variant px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">category</span>
              <div>
                <h3 className="font-h3 text-h3 text-on-surface">{t('admin.products.catalog.categories.title')}</h3>
                <p className="text-body-sm text-secondary">{t('admin.products.catalog.categories.description')}</p>
              </div>
            </div>
          </header>

          <div className="max-h-[340px] min-h-40 overflow-y-auto">
            {renderListState(isCategoryLoading, categoryError, categories.length === 0, 'categories')}
            {!isCategoryLoading && !categoryError && categories.length > 0 && (
              <ul className="divide-y divide-outline-variant/60">
                {categories.map((category) => (
                  <li key={category.id} className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-surface-container-lowest">
                    <div className="min-w-0">
                      <p className="font-medium text-on-surface">{category.name}</p>
                      <p className="mt-0.5 line-clamp-2 text-body-sm text-secondary">
                        {category.description || t('admin.products.catalog.no_description')}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        className="inline-flex rounded p-2 text-secondary hover:bg-primary-fixed hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
                        disabled={isBusy}
                        aria-label={t('admin.products.catalog.edit_category_label', { name: category.name })}
                        title={t('admin.products.catalog.edit_category')}
                        onClick={() => startEditingCategory(category)}
                      >
                        <span className="material-symbols-outlined text-[19px]">edit</span>
                      </button>
                      <button
                        type="button"
                        className="inline-flex rounded p-2 text-error hover:bg-error-container/60 focus:outline-none focus:ring-2 focus:ring-error/30 disabled:opacity-50"
                        disabled={isBusy}
                        aria-label={t('admin.products.catalog.delete_category_label', { name: category.name })}
                        title={t('admin.products.catalog.delete_category')}
                        onClick={() => setDeleteTarget({ kind: 'category', entity: category })}
                      >
                        <span className="material-symbols-outlined text-[19px]">delete</span>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <form className="border-t border-outline-variant bg-surface-container-lowest p-4" onSubmit={submitCategory}>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h4 className="font-label-md text-label-md font-semibold text-on-surface">
                {editingCategory ? t('admin.products.catalog.edit_named', { name: editingCategory.name }) : t('admin.products.catalog.add_category')}
              </h4>
              {editingCategory && (
                <button type="button" className="text-body-sm text-primary hover:underline" onClick={resetCategoryForm} disabled={isBusy}>
                  {t('admin.products.catalog.cancel_edit')}
                </button>
              )}
            </div>
            <div className="space-y-3">
              <label className="block font-label-sm text-label-sm text-on-surface">
                {t('admin.products.catalog.category_name')} <span className="text-error">*</span>
                <input
                  className="mt-1 h-11 w-full rounded-xl border border-outline-variant bg-white px-3 text-body-md focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:bg-surface-container"
                  value={categoryName}
                  disabled={isBusy}
                  onChange={(event) => {
                    setCategoryName(event.target.value);
                    setCategoryFormError('');
                  }}
                  placeholder={t('admin.products.catalog.category_placeholder')}
                />
              </label>
              <label className="block font-label-sm text-label-sm text-on-surface">
                {t('admin.products.catalog.category_description')} <span className="text-error">*</span>
                <textarea
                  className="mt-1 min-h-20 w-full resize-y rounded-xl border border-outline-variant bg-white px-3 py-2 text-body-md focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:bg-surface-container"
                  value={categoryDescription}
                  disabled={isBusy}
                  onChange={(event) => {
                    setCategoryDescription(event.target.value);
                    setCategoryFormError('');
                  }}
                  placeholder={t('admin.products.catalog.description_placeholder')}
                />
              </label>
              {categoryFormError && <p className="text-body-sm text-error" role="alert">{categoryFormError}</p>}
              <button
                type="submit"
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 font-label-md text-on-primary hover:bg-on-primary-fixed-variant disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                disabled={isBusy}
              >
                {pendingAction === 'category-save' && <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>}
                {pendingAction === 'category-save' ? t('admin.products.catalog.saving') : editingCategory ? t('admin.products.catalog.save_changes') : t('admin.products.catalog.add_category')}
              </button>
            </div>
          </form>
        </article>

        <article className="min-w-0">
          <header className="border-b border-outline-variant px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">sell</span>
              <div>
                <h3 className="font-h3 text-h3 text-on-surface">{t('admin.products.catalog.brands.title')}</h3>
                <p className="text-body-sm text-secondary">{t('admin.products.catalog.brands.description')}</p>
              </div>
            </div>
          </header>

          <div className="max-h-[340px] min-h-40 overflow-y-auto">
            {renderListState(isBrandLoading, brandError, brands.length === 0, 'brands')}
            {!isBrandLoading && !brandError && brands.length > 0 && (
              <ul className="divide-y divide-outline-variant/60">
                {brands.map((brand) => (
                  <li key={brand.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-surface-container-lowest">
                    <p className="min-w-0 truncate font-medium text-on-surface">{brand.name}</p>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        className="inline-flex rounded p-2 text-secondary hover:bg-primary-fixed hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
                        disabled={isBusy}
                        aria-label={t('admin.products.catalog.edit_brand_label', { name: brand.name })}
                        title={t('admin.products.catalog.edit_brand')}
                        onClick={() => startEditingBrand(brand)}
                      >
                        <span className="material-symbols-outlined text-[19px]">edit</span>
                      </button>
                      <button
                        type="button"
                        className="inline-flex rounded p-2 text-error hover:bg-error-container/60 focus:outline-none focus:ring-2 focus:ring-error/30 disabled:opacity-50"
                        disabled={isBusy}
                        aria-label={t('admin.products.catalog.delete_brand_label', { name: brand.name })}
                        title={t('admin.products.catalog.delete_brand')}
                        onClick={() => setDeleteTarget({ kind: 'brand', entity: brand })}
                      >
                        <span className="material-symbols-outlined text-[19px]">delete</span>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <form className="border-t border-outline-variant bg-surface-container-lowest p-4" onSubmit={submitBrand}>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h4 className="font-label-md text-label-md font-semibold text-on-surface">
                {editingBrand ? t('admin.products.catalog.edit_named', { name: editingBrand.name }) : t('admin.products.catalog.add_brand')}
              </h4>
              {editingBrand && (
                <button type="button" className="text-body-sm text-primary hover:underline" onClick={resetBrandForm} disabled={isBusy}>
                  {t('admin.products.catalog.cancel_edit')}
                </button>
              )}
            </div>
            <label className="block font-label-sm text-label-sm text-on-surface">
              {t('admin.products.catalog.brand_name')} <span className="text-error">*</span>
              <input
                className="mt-1 h-11 w-full rounded-xl border border-outline-variant bg-white px-3 text-body-md focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:bg-surface-container"
                value={brandName}
                disabled={isBusy}
                onChange={(event) => {
                  setBrandName(event.target.value);
                  setBrandFormError('');
                }}
                placeholder={t('admin.products.catalog.brand_placeholder')}
              />
            </label>
            {brandFormError && <p className="mt-3 text-body-sm text-error" role="alert">{brandFormError}</p>}
            <button
              type="submit"
              className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 font-label-md text-on-primary hover:bg-on-primary-fixed-variant disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              disabled={isBusy}
            >
              {pendingAction === 'brand-save' && <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>}
              {pendingAction === 'brand-save' ? t('admin.products.catalog.saving') : editingBrand ? t('admin.products.catalog.save_changes') : t('admin.products.catalog.add_brand')}
            </button>
          </form>
        </article>
      </div>

      {deleteTarget && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isBusy) setDeleteTarget(null);
          }}
        >
          <section
            className="w-full max-w-md rounded-[20px] border border-outline-variant bg-white p-5 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-catalog-entity-title"
            aria-describedby="delete-catalog-entity-description"
          >
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined rounded-full bg-error-container p-2 text-error">delete</span>
              <div>
                <h3 id="delete-catalog-entity-title" className="font-h3 text-h3 text-on-surface">
                  {t(deleteTarget.kind === 'brand' ? 'admin.products.catalog.delete_dialog.brand_title' : 'admin.products.catalog.delete_dialog.category_title')}
                </h3>
                <p id="delete-catalog-entity-description" className="mt-2 text-body-md text-secondary">
                  {t('admin.products.catalog.delete_dialog.description', { name: deleteTargetName })}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                autoFocus
                type="button"
                disabled={isBusy}
                className="min-h-10 rounded-xl border border-outline-variant bg-white px-4 font-label-md text-secondary hover:bg-surface-container-low disabled:opacity-60"
                onClick={() => setDeleteTarget(null)}
              >
                {t('admin.products.catalog.cancel')}
              </button>
              <button
                type="button"
                disabled={isBusy}
                className="min-h-10 rounded-xl bg-error px-4 font-label-md text-on-error hover:bg-on-error-container disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => void confirmDelete()}
              >
                {pendingAction === deletePendingKey ? t('admin.products.catalog.deleting') : t('admin.products.catalog.confirm_delete')}
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
};

export default CatalogManagementSection;
